import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Popover, type PopoverStrategy, type PopoverTriggerApi } from './Popover';

export interface MenuItem {
  /** Stable key + identity for the item. */
  id: string;
  label: ReactNode;
  /** Invoked on click / Enter. Omitted or `disabled` → inert item. */
  onSelect?: () => void;
  disabled?: boolean;
  /** Destructive styling (e.g. Delete). */
  danger?: boolean;
  'data-testid'?: string;
}

export type MenuTriggerApi = PopoverTriggerApi;

export interface MenuProps {
  /** Render the trigger element; spread the supplied api onto it. */
  trigger: (api: MenuTriggerApi) => ReactNode;
  items: MenuItem[];
  /** Accessible label for the popover (`role="menu"`). */
  label: string;
  /** Horizontal edge the popover aligns to. Default `end`. */
  align?: 'start' | 'end';
  /** Positioning strategy. `fixed` portals the panel to `document.body` so it
   *  is not clipped by an `overflow: hidden` ancestor (e.g. board cards).
   *  Default `absolute`. */
  strategy?: PopoverStrategy;
  className?: string;
  'data-testid'?: string;
}

/**
 * Trigger-anchored overflow menu. Composes `<Popover>` with `role="menu"`,
 * `role="menuitem"` items, ArrowUp/ArrowDown roving focus, and first-item
 * focus on open. Generalises the shell-private `IdentityFlyout` (`pn-flyout`)
 * into a reusable primitive.
 */
export function Menu({
  trigger,
  items,
  label,
  align = 'end',
  strategy = 'absolute',
  className,
  'data-testid': testId
}: MenuProps) {
  const popRef = useRef<HTMLDivElement>(null);

  function onMenuKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const focusable = Array.from(
      popRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"]:not([disabled])'
      ) ?? []
    );
    if (focusable.length === 0) return;
    const idx = focusable.indexOf(
      document.activeElement as HTMLButtonElement
    );
    const next =
      e.key === 'ArrowDown'
        ? focusable[(idx + 1) % focusable.length]
        : focusable[(idx - 1 + focusable.length) % focusable.length];
    next?.focus();
  }

  function focusFirstItem() {
    const first = popRef.current?.querySelector<HTMLButtonElement>(
      '[role="menuitem"]:not([disabled])'
    );
    first?.focus();
  }

  const wrapCls = ['pn-menu-wrap', className].filter(Boolean).join(' ');

  return (
    <Popover
      role="menu"
      trigger={trigger}
      label={label}
      align={align}
      strategy={strategy}
      className={wrapCls}
      data-testid={testId}
      panelRef={popRef}
      onPanelKeyDown={onMenuKeyDown}
      onPanelOpened={focusFirstItem}
    >
      {({ close }) => (
        <>
          {items.map((item) => {
            const itemCls = [
              'pn-menu-item',
              item.danger ? 'pn-menu-item-danger' : null
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className={itemCls}
                disabled={item.disabled}
                data-testid={item['data-testid']}
                onClick={() => {
                  close();
                  item.onSelect?.();
                }}
              >
                {item.label}
              </button>
            );
          })}
        </>
      )}
    </Popover>
  );
}
